import { useMemoizedFn } from 'ahooks';
import { concat, find } from 'lodash-es';
import { useHistory } from 'umi';
import { getExpectedTemplate } from '@/pages/aigc/utils/utils';
import { useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import type { TDatabrainFile } from '../types';

const CHAT_SCROLL_CONTAINER = '.dba-chat-page';
const MESSAGE_ANCHOR_OFFSET = 30;
const SCROLL_MAX_FRAMES = 60;

/** 历史消息 DOM id 前缀，与 ChatPage SingleQA 一致 */
const messageAnchorId = (messageId: string): string => `dba-qa-${messageId}`;

/**
 * 跳转后轮询直到消息节点出现，再滚到锚点（不依赖 ChatPage 读 URL）
 * @param messageId - 目标消息 id
 */
const scrollToMessageAnchor = (messageId: string): void => {
  if (!messageId) {
    return;
  }
  let frames = 0;
  const tick = (): void => {
    const container = document.querySelector(CHAT_SCROLL_CONTAINER);
    const el = document.getElementById(messageAnchorId(messageId));
    if (container instanceof HTMLElement && el) {
      container.scrollTop +=
        el.getBoundingClientRect().top -
        container.getBoundingClientRect().top -
        MESSAGE_ANCHOR_OFFSET;
      return;
    }
    frames += 1;
    if (frames < SCROLL_MAX_FRAMES) {
      requestAnimationFrame(tick);
    }
  };
  requestAnimationFrame(tick);
};

/**
 * 文件库跳转原对话：拼 URL、push、消息锚点滚动均在此 Hook 内完成。
 * @returns openOriginalChat - 打开文件所属会话并定位到对应消息
 */
export const useOpenOriginalChat = () => {
  const history = useHistory();
  const { chatList, bookMarkedChats } = useChatList({ manual: true });

  const openOriginalChat = useMemoizedFn((file: TDatabrainFile): void => {
    const session = find(
      concat(chatList, bookMarkedChats),
      item => item.session_id === file.session_id,
    );
    const template = getExpectedTemplate(session);
    const templateParam = template ? `&template=${template}` : '';
    const messageParam = file.message_id ? `&messageId=${file.message_id}` : '';
    history.push(
      `/agent/chat?sessionId=${file.session_id}${templateParam}${messageParam}`,
    );
    if (file.message_id) {
      scrollToMessageAnchor(file.message_id);
    }
  });

  return { openOriginalChat };
};
